import { Form, Formik } from 'formik';
import { type FC } from 'react';

import { OrderType } from '@/api/order/models/OrderType';
import { useCurrentUserInfo } from '@/auth/hooks';
import { Show } from '@/components/Show';
import { useTexts } from '@/hooks/useTexts';
import { useOrderSteps } from '@/pages/Order/components/OrderStepsProvider';
import { useOrderPageContext } from '@/pages/Order/OrderPageContext';
import { FundOrder } from '@/pages/Order/types/Order';

import { useFundPortfolios } from '../../hooks/useFundPortfolios';
import { RedemptionOrderFormValues } from '../../types/RedemptionOrderFormValues';
import { mapRedemptionFundStatesToOrderPlacements } from '../../utils/mapRedemptionFundStatesToOrderPlacements';
import { useCreateRedemptionValidationSchema } from '../../validationSchemas/hooks/useCreateRedemptionValidationSchema';
import { useInitialRedemptionFormValues } from './hooks/useInitialRedemptionFormValues';
import { RedemptionFormContent } from './RedemptionFormContent';

export const RedemptionOrderForm: FC = () => {
  const { orders } = useTexts();
  const userInfo = useCurrentUserInfo();

  const { completeCurrentStep } = useOrderSteps();
  const { setCurrentOrder } = useOrderPageContext<FundOrder>();

  const portfolios = useFundPortfolios(OrderType.Redemption);
  const hasNoPortfoliosWithHoldings = portfolios.length === 0;

  const initialValues = useInitialRedemptionFormValues();
  const validationSchema = useCreateRedemptionValidationSchema();

  const handleSubmit = (values: RedemptionOrderFormValues): void => {
    const orderPlacements = mapRedemptionFundStatesToOrderPlacements(
      values.fromFunds,
      values.payments,
    );

    const fundOrder: FundOrder = {
      orderType: OrderType.Redemption,
      userId: userInfo.userCmId || userInfo.signature!,
      portfolioId: values.fromPortfolioShortName,
      signatories: [values.signer, values.cosigner].filter(
        (value) => value !== null && value !== undefined,
      ),
      payments: values.payments,
      orderPlacements,
      signingProvider: values.signingProvider,
    };

    setCurrentOrder(fundOrder);
    completeCurrentStep();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <Form data-test="redemption-order-form">
        <Show when={hasNoPortfoliosWithHoldings}>
          <h3 data-test="no-holdings-to-sell">
            {orders.form.redemption.noHoldingsToSell}
          </h3>
        </Show>
        <RedemptionFormContent />
      </Form>
    </Formik>
  );
};
